import {
    Box,
    Collapse,
    List,
    ListItem,
    Stack,
    styled,
    Typography,
    useTheme,
} from '@mui/material';
import { translateBlock } from 'i18n';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetInitialisation } from './hook';
import Loading from 'components/Loading';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorIcon from '@mui/icons-material/Error';
import HttpErrorWrapper from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/HttpErrorWrapper';
import NotFound from 'components/Errors/NotFound';
import StyBudgetContent from 'routes/BudgetModule/components/shared/BudgetContent';
import { formatDate } from 'helpers/dateHelper';
import { InitialisationStatus } from './type';
import GroupActionButton from '../shared/GroupActionButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteInitialisationModal from './DeleteInitialisationModal';
import { useHasAccessBudgetsGestion, useIsSupportUser } from 'hooks/user';

const StyCheckedIcon = styled(CheckCircleRoundedIcon, {
    name: 'StyCheckedIcon',
})(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.green,
}));

const StyErrorIcon = styled(ErrorIcon, {
    name: 'StyCheckedIcon',
})(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.red,
}));

export default function Initialisation(): React.ReactElement {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSupportUser = useIsSupportUser();
    const hasBudgetsGestion = useHasAccessBudgetsGestion();
    const { initialisationId } = useParams<{ initialisationId: string }>();
    const {
        data: initialisation,
        isLoading,
        error,
        isError,
    } = useGetInitialisation(
        initialisationId ? parseInt(initialisationId, 10) : -1,
    );

    const [showDeleteInitialisationModal, setShowDeleteInitialisationModal] =
        useState(false);

    const deleteInitialisationModal = (
        <DeleteInitialisationModal
            isModalShown={showDeleteInitialisationModal}
            setShowModal={setShowDeleteInitialisationModal}
            initialisationId={
                initialisationId ? parseInt(initialisationId, 10) : -1
            }
        />
    );

    const openDeleteInitialisationModal = () => {
        setShowDeleteInitialisationModal(true);
    };

    const renderFormattedItem = (
        count: number,
        key: string,
        pl: string | number = 'Opx',
        extraOptions = {},
    ): React.ReactElement => (
        <ListItem sx={{ pl, alignItems: 'start' }} disablePadding>
            <Trans
                i18nKey={key}
                count={count}
                tOptions={extraOptions}
                components={{
                    bold: (
                        <strong
                            style={{
                                color: theme.palette.primary.main,
                                paddingRight: '0.5ch',
                            }}
                        />
                    ),
                }}
            />
        </ListItem>
    );

    if (
        (error && error.status === 404) ||
        !(isSupportUser || hasBudgetsGestion)
    ) {
        return <NotFound />;
    }
    if (isLoading || !initialisation) {
        return <Loading />;
    }

    const hasError =
        initialisation.status == InitialisationStatus.FAILED ||
        initialisation.executionSummary.some(
            (summary) => summary.logs.isError === true,
        );

    const renderSuccessContent = (
        <>
            <Typography variant="h5" sx={{ paddingBottom: '1.5em' }}>
                {t('adminBudgetSpace.summary.header')}
            </Typography>
            <Stack sx={{ pl: '10px' }}>
                {initialisation.executionSummary.map((summary) => (
                    <Stack
                        key={summary.budgetProposalId}
                        sx={{ paddingBottom: '1.5em' }}
                    >
                        <Typography
                            variant="body1Bold"
                            sx={{
                                paddingBottom: '1.5em',
                                color: theme.palette.primary.main,
                            }}
                        >
                            {summary.budgetProposalName}
                        </Typography>
                        <List disablePadding>
                            {renderFormattedItem(
                                summary.logs.inscriptionsCreated,
                                'adminBudgetSpace.summary.inscription',
                            )}
                            {renderFormattedItem(
                                summary.logs.orphans,
                                'adminBudgetSpace.summary.orphan',
                            )}
                            {renderFormattedItem(
                                summary.logs.skipped.missingResource +
                                    summary.logs.skipped.badResource,
                                'adminBudgetSpace.summary.skipped',
                            )}
                            <Collapse
                                component="li"
                                in
                                timeout="auto"
                                unmountOnExit
                            >
                                <List disablePadding>
                                    {renderFormattedItem(
                                        summary.logs.skipped.missingResource,
                                        'adminBudgetSpace.summary.missingResource',
                                        '3ch',
                                        {
                                            resource: translateBlock(
                                                summary.resourceType,
                                                true,
                                            ).toLocaleLowerCase(),
                                        },
                                    )}
                                    {renderFormattedItem(
                                        summary.logs.skipped.badResource,
                                        'adminBudgetSpace.summary.badResource',
                                        '3ch',
                                        {
                                            resource: translateBlock(
                                                summary.resourceType,
                                                true,
                                            ).toLocaleLowerCase(),
                                        },
                                    )}
                                </List>
                            </Collapse>
                            {renderFormattedItem(
                                summary.logs.deleted,
                                'adminBudgetSpace.summary.deleted',
                            )}
                        </List>
                    </Stack>
                ))}
            </Stack>
            <Box sx={{ pt: '2em', pb: '2em' }}>
                <GroupActionButton
                    title={t('adminBudgetSpace.deleteInitialisation.title')}
                    onClick={openDeleteInitialisationModal}
                    icon={<DeleteIcon />}
                    enabled={
                        initialisation.hasInscriptionLinked &&
                        !initialisation.deleted
                    }
                    tooltipTitle={
                        initialisation.deleted
                            ? t(
                                  'adminBudgetSpace.deleteInitialisation.isDeleted',
                              )
                            : t(
                                  'adminBudgetSpace.deleteInitialisation.hasNoInscriptionsLinked',
                              )
                    }
                />
                {showDeleteInitialisationModal && deleteInitialisationModal}
            </Box>
        </>
    );

    const renderErrorContent = (
        <>
            <Typography variant="h5" sx={{ whiteSpace: 'normal', pt: '10px' }}>
                {t('adminBudgetSpace.summary.error')}
            </Typography>
            <Stack>
                {initialisation.executionSummary
                    .filter((summary) => summary.logs.isError)
                    .map((summary) => (
                        <Typography
                            sx={{ whiteSpace: 'pre-line' }}
                            key={summary.budgetProposalId}
                        >
                            {summary.logs.error}
                        </Typography>
                    ))}
            </Stack>
        </>
    );

    return (
        <StyBudgetContent>
            <HttpErrorWrapper error={error} isError={isError}>
                <Typography
                    variant="h4"
                    sx={{
                        paddingBottom: '2em',
                        color: theme.palette.primary.main,
                    }}
                >
                    {t('adminBudgetSpace.summary.title', {
                        data: formatDate(new Date(initialisation.updatedAt)),
                    })}
                    <Box sx={{ pl: '10px' }} component="span">
                        {hasError ? <StyErrorIcon /> : <StyCheckedIcon />}
                    </Box>
                </Typography>
                {hasError ? renderErrorContent : renderSuccessContent}
            </HttpErrorWrapper>
        </StyBudgetContent>
    );
}
